import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, Row, Col, Form, Button, Carousel } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/api';
import { usePageLoader } from '../context/PageLoaderContext';
import ProductCard from '../components/product/ProductCard';
import ComboCard from '../components/product/ComboCard';
import FilterButton from '../components/product/FilterButton';
import SizeButton from '../components/product/SizeButton';
import PriceRangeFilter from '../components/product/PriceRangeFilter';
import ColorChip from '../components/product/ColorChip';
import MaterialCheckbox from '../components/product/MaterialCheckbox';
import CachedImage from '../components/CachedImage';
import { getVisiblePages } from '../utils/pagination';
import './Products.css';

/** Build products page URL from banner category link params (supports L1, L2, L3). */
const getBannerProductsUrl = (banner) => {
  if (!banner?.category_id) return null;
  const cat = banner.link_category_id != null ? banner.link_category_id : banner.category_id;
  const sub = banner.link_subcategory_id ?? null;
  const l3 = banner.link_l3_category_id ?? null;
  const params = new URLSearchParams();
  params.set('category', String(cat));
  if (sub) params.set('subcategory', String(sub));
  if (l3) params.set('l3', String(l3));
  return `/products?${params.toString()}`;
};

const Products = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products for category counts
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [textures, setTextures] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [saleBanners, setSaleBanners] = useState([]);
  const [combos, setCombos] = useState([]);
  const [mixedPageItems, setMixedPageItems] = useState([]); // [{ kind, data }] for interleaved grid
  const [loading, setLoading] = useState(true);
  const { setPageLoading } = usePageLoader();

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  // Show filters by default on desktop, hidden on mobile
  const [showFilters, setShowFilters] = useState(false);
  const wasDesktopRef = useRef(typeof window !== 'undefined' && window.innerWidth >= 993);
  const [sortBy, setSortBy] = useState('featured');
  // Initialize filter states from URL
  const initialFilterParam = searchParams.get('filter');
  const [showOnlySale, setShowOnlySale] = useState(initialFilterParam === 'sale');
  const [showOnlyBestsellers, setShowOnlyBestsellers] = useState(initialFilterParam === 'bestsellers');
  const [showOnlyNew, setShowOnlyNew] = useState(initialFilterParam === 'new');
  const [showOnlyCombo, setShowOnlyCombo] = useState(initialFilterParam === 'combo');
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Initialize filters from URL query params
  const getInitialFilters = () => {
    const sizes = searchParams.get('sizes') ? searchParams.get('sizes').split(',') : [];
    const colors = searchParams.get('colors') ? searchParams.get('colors').split(',').map(c => parseInt(c)) : [];
    const materials = searchParams.get('materials') ? searchParams.get('materials').split(',') : [];
    const productTypes = searchParams.get('product_types') ? searchParams.get('product_types').split(',') : [];
    const textures = searchParams.get('textures') ? searchParams.get('textures').split(',') : [];
    const occasions = searchParams.get('occasions') ? searchParams.get('occasions').split(',') : [];
    const patterns = searchParams.get('patterns') ? searchParams.get('patterns').split(',') : [];
    const minPriceRaw = searchParams.get('minPrice') ? parseInt(searchParams.get('minPrice')) : null;
    const minPrice = minPriceRaw != null ? Math.min(9999, Math.max(0, minPriceRaw)) : null;
    const maxPriceRaw = searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')) : null;
    const maxPrice = maxPriceRaw != null ? Math.min(10000, Math.max(0, maxPriceRaw)) : null;
    
    return {
      category_id: searchParams.get('category') || '',
      subcategory_id: searchParams.get('subcategory') || '',
      l3_category_id: searchParams.get('l3') || '',
      search: searchParams.get('search') || '',
      sizes,
      colors,
      materials,
      productTypes,
      textures,
      occasions,
      patterns,
      minPrice,
      maxPrice,
    };
  };

  const initialFilters = getInitialFilters();
  const [priceRange, setPriceRange] = useState({
    minPrice: initialFilters.minPrice,
    maxPrice: initialFilters.maxPrice,
  });
  const [filters, setFilters] = useState(initialFilters);
  
  // Sync filters with URL params when they change (e.g., from navbar search)
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam === 'bestsellers') {
      setShowOnlyBestsellers(true);
      setShowOnlySale(false);
      setShowOnlyNew(false);
      setShowOnlyCombo(false);
    } else if (filterParam === 'sale') {
      setShowOnlySale(true);
      setShowOnlyBestsellers(false);
      setShowOnlyNew(false);
      setShowOnlyCombo(false);
    } else if (filterParam === 'new') {
      setShowOnlyNew(true);
      setShowOnlySale(false);
      setShowOnlyBestsellers(false);
      setShowOnlyCombo(false);
    } else if (filterParam === 'combo') {
      setShowOnlyCombo(true);
      setShowOnlySale(false);
      setShowOnlyBestsellers(false);
      setShowOnlyNew(false);
      setSortBy((prev) => (prev === 'best_selling' || prev === 'featured' ? 'newest' : prev));
    } else {
      setShowOnlySale(false);
      setShowOnlyBestsellers(false);
      setShowOnlyNew(false);
      setShowOnlyCombo(false);
    }
    
    const newFilters = getInitialFilters();
    setFilters(newFilters);
    setPriceRange({
      minPrice: newFilters.minPrice,
      maxPrice: newFilters.maxPrice,
    });
    setPage(1); // Reset to first page when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Sales banners only on /products?filter=sale
  useEffect(() => {
    if (!showOnlySale) {
      setSaleBanners([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/banners?is_published=true&is_sales=true&limit=50');
        if (!cancelled) setSaleBanners(res.banners || []);
      } catch (err) {
        console.error('Error fetching sale banners:', err);
        if (!cancelled) setSaleBanners([]);
      }
    })();
    return () => { cancelled = true; };
  }, [showOnlySale]);
  
  // Collapsible filter states - all start expanded (false = expanded/visible)
  const [collapsedFilters, setCollapsedFilters] = useState({
    category: false,
    size: false,
    price: false,
    color: false,
    material: false,
    productType: false,
    texture: false,
    occasion: false,
    pattern: false,
  });
  // Which category (L1) and subcategory (L2) rows are expanded in the tree
  const [expandedCategoryTree, setExpandedCategoryTree] = useState({});
  const toggleCategoryTreeExpand = (id) => {
    setExpandedCategoryTree((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      // Combos listing: /products?filter=combo
      if (showOnlyCombo) {
        const params = new URLSearchParams({
          is_active: 'true',
          page: page.toString(),
          limit: '30',
        });
        const response = await api.get(`/combos?${params}`);
        let list = response.combos || [];
        if (sortBy === 'price_low') {
          list = [...list].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        } else if (sortBy === 'price_high') {
          list = [...list].sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        } else if (sortBy === 'newest') {
          list = [...list].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        }
        setCombos(list);
        setProducts([]);
        setMixedPageItems([]);
        setTotalProducts(response.pagination?.total || list.length);
        setTotalPages(response.pagination?.pages || 1);
        return;
      }

      let url = '/products?is_active=true&expand_colors=true&exclude_combo_products=true';

      if (filters.category_id) url += `&category_id=${filters.category_id}`;
      if (filters.subcategory_id) url += `&subcategory_id=${filters.subcategory_id}`;
      if (filters.l3_category_id) url += `&l3_category_id=${filters.l3_category_id}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
      if (showOnlySale) url += `&on_sale=true`;
      if (showOnlyBestsellers) url += `&is_bestseller=true`;
      if (showOnlyNew) url += `&is_new=true`;

      // Add size filter
      if (filters.sizes.length > 0) {
        url += `&sizes=${filters.sizes.join(',')}`;
      }

      // Add color filter
      if (filters.colors.length > 0) {
        url += `&colors=${filters.colors.join(',')}`;
      }

      // Add material filter
      if (filters.materials.length > 0) {
        url += `&materials=${filters.materials.join(',')}`;
      }

      // Add product type filter
      if (filters.productTypes && filters.productTypes.length > 0) {
        url += `&product_types=${filters.productTypes.join(',')}`;
      }

      // Add texture filter
      if (filters.textures && filters.textures.length > 0) {
        url += `&textures=${filters.textures.join(',')}`;
      }

      // Add occasion filter
      if (filters.occasions && filters.occasions.length > 0) {
        url += `&occasions=${filters.occasions.join(',')}`;
      }

      // Add pattern filter
      if (filters.patterns && filters.patterns.length > 0) {
        url += `&patterns=${filters.patterns.join(',')}`;
      }

      // Add price filter (only if explicitly set)
      if (filters.minPrice !== null && filters.minPrice !== undefined) {
        url += `&minPrice=${filters.minPrice}`;
      }
      if (filters.maxPrice !== null && filters.maxPrice !== undefined) {
        url += `&maxPrice=${filters.maxPrice}`;
      }

      // Add pagination — when mixing combos into featured/default order, fetch a wide page
      // and slice client-side so combo positions from Ordering are respected.
      const includeCombosInListing =
        !showOnlySale && !showOnlyBestsellers && !showOnlyNew;
      const mixClientSide = includeCombosInListing;
      const pageLimit = mixClientSide ? 1000 : 30;
      url += `&page=${mixClientSide ? 1 : page}&limit=${pageLimit}`;

      const [response, combosRes] = await Promise.all([
        api.get(url),
        includeCombosInListing
          ? api.get('/combos?is_active=true&limit=100').catch(() => ({ combos: [] }))
          : Promise.resolve({ combos: [] }),
      ]);

      let fetchedProducts = response.products || [];
      let fetchedCombos = combosRes.combos || [];

      const sortKey = (item) => {
        // null/undefined must NOT become 0 (Number(null) === 0) or unordered
        // items jump ahead of product_listing_order.
        if (item?.listing_sort_order == null || item.listing_sort_order === '') {
          return Number.POSITIVE_INFINITY;
        }
        const n = Number(item.listing_sort_order);
        return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
      };

      // Sort products (client-side). "featured" keeps admin listing order from API.
      if (sortBy === 'best_selling') {
        fetchedProducts = fetchedProducts.sort(
          (a, b) => (b.is_bestseller ? 1 : 0) - (a.is_bestseller ? 1 : 0)
        );
        fetchedCombos = [...fetchedCombos];
      } else if (sortBy === 'price_low') {
        fetchedProducts = fetchedProducts.sort(
          (a, b) => (a.final_price || a.price || 0) - (b.final_price || b.price || 0)
        );
        fetchedCombos = [...fetchedCombos].sort(
          (a, b) => parseFloat(a.price) - parseFloat(b.price)
        );
      } else if (sortBy === 'price_high') {
        fetchedProducts = fetchedProducts.sort(
          (a, b) => (b.final_price || b.price || 0) - (a.final_price || a.price || 0)
        );
        fetchedCombos = [...fetchedCombos].sort(
          (a, b) => parseFloat(b.price) - parseFloat(a.price)
        );
      } else if (sortBy === 'newest') {
        fetchedProducts = fetchedProducts.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        fetchedCombos = [...fetchedCombos].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      } else {
        // featured / default: admin listing order (products + combos interleaved)
        fetchedProducts = [...fetchedProducts].sort(
          (a, b) => sortKey(a) - sortKey(b) || String(a.name || '').localeCompare(String(b.name || ''))
        );
        fetchedCombos = [...fetchedCombos].sort(
          (a, b) => sortKey(a) - sortKey(b) || String(a.title || '').localeCompare(String(b.title || ''))
        );
      }

      if (mixClientSide) {
        let merged;
        if (sortBy === 'featured' || !sortBy) {
          merged = [
            ...fetchedProducts.map((p) => ({ kind: 'product', sort: sortKey(p), data: p })),
            ...fetchedCombos.map((c) => ({ kind: 'combo', sort: sortKey(c), data: c })),
          ].sort(
            (a, b) =>
              a.sort - b.sort ||
              (a.kind === b.kind ? 0 : a.kind === 'combo' ? 1 : -1) ||
              String(a.data.name || a.data.title || '').localeCompare(
                String(b.data.name || b.data.title || '')
              )
          );
        } else if (sortBy === 'price_low' || sortBy === 'price_high' || sortBy === 'newest' || sortBy === 'best_selling') {
          // Interleave by the same sort criteria instead of forcing combos first
          const priceOf = (item) =>
            item.kind === 'combo'
              ? parseFloat(item.data.price) || 0
              : parseFloat(item.data.final_price || item.data.price) || 0;
          const dateOf = (item) => new Date(item.data.created_at || 0).getTime();
          merged = [
            ...fetchedProducts.map((p) => ({ kind: 'product', data: p })),
            ...fetchedCombos.map((c) => ({ kind: 'combo', data: c })),
          ];
          if (sortBy === 'price_low') merged.sort((a, b) => priceOf(a) - priceOf(b));
          else if (sortBy === 'price_high') merged.sort((a, b) => priceOf(b) - priceOf(a));
          else if (sortBy === 'newest') merged.sort((a, b) => dateOf(b) - dateOf(a));
          else if (sortBy === 'best_selling') {
            merged.sort((a, b) => {
              const ba = a.kind === 'product' && a.data.is_bestseller ? 1 : 0;
              const bb = b.kind === 'product' && b.data.is_bestseller ? 1 : 0;
              return bb - ba;
            });
          }
        } else {
          merged = [
            ...fetchedProducts.map((p) => ({ kind: 'product', data: p })),
            ...fetchedCombos.map((c) => ({ kind: 'combo', data: c })),
          ];
        }

        const pageSize = 30;
        const total = merged.length;
        const pages = Math.max(1, Math.ceil(total / pageSize));
        const start = (page - 1) * pageSize;
        const pageItems = merged.slice(start, start + pageSize);
        setMixedPageItems(pageItems);
        setCombos(pageItems.filter((i) => i.kind === 'combo').map((i) => i.data));
        setProducts(pageItems.filter((i) => i.kind === 'product').map((i) => i.data));
        setTotalProducts(total);
        setTotalPages(pages);
      } else {
        setMixedPageItems([]);
        setCombos([]);
        setProducts(fetchedProducts);
        const productTotal = response.pagination?.total || fetchedProducts.length;
        setTotalProducts(productTotal);
        setTotalPages(
          response.pagination?.pages ||
            Math.ceil((response.pagination?.total || fetchedProducts.length) / 30)
        );
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, showOnlySale, showOnlyBestsellers, showOnlyNew, showOnlyCombo, page]);

  useEffect(() => {
    fetchCategories();
    fetchColors();
    fetchSizes();
    fetchMaterials();
    fetchProductTypes();
    fetchTextures();
    fetchOccasions();
    fetchPatterns();
    fetchAllProductsForCounts();
    
    // Set initial filter visibility based on screen size
    // On desktop (>= 993px), show filters by default
    // On mobile (< 993px), hide filters (slide-in panel)
    if (window.innerWidth >= 993) {
      setShowFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllProductsForCounts = async () => {
    try {
      // Fetch all active products for accurate category counts
      const response = await api.get('/products?is_active=true&limit=1000');
      setAllProducts(response.products || []);
    } catch (error) {
      console.error('Error fetching all products for category counts:', error);
      // Fallback to using filtered products if fetch fails
      setAllProducts(products);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Handle window resize to update filter visibility (only when crossing desktop/mobile breakpoint)
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 993;
      // Only close filters when actually transitioning from desktop to mobile (e.g. rotating device).
      // Do NOT close on every mobile resize (e.g. virtual keyboard opening) so min/max price inputs don't dismiss the panel.
      if (wasDesktopRef.current && !isDesktop) {
        setShowFilters(false);
        wasDesktopRef.current = false;
      } else if (isDesktop) {
        wasDesktopRef.current = true;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories?is_active=true');
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchColors = async () => {
    try {
      const response = await api.get('/colors?is_active=true');
      setColors(response.colors || []);
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  const fetchSizes = async () => {
    try {
      // Fetch a sample of products to get unique sizes from variants
      // We'll fetch a reasonable number and extract sizes
      const response = await api.get('/products?is_active=true&limit=100');
      const allProducts = response.products || [];
      const allSizes = new Set();
      
      // Fetch variants for a sample of products (first 20 to avoid too many requests)
      const sampleProducts = allProducts.slice(0, 20);
      for (const product of sampleProducts) {
        try {
          const productResponse = await api.get(`/products/${product.id}`);
          if (productResponse.variants) {
            productResponse.variants.forEach((variant) => {
              if (variant.size) {
                allSizes.add(variant.size);
              }
            });
          }
        } catch (err) {
          // Skip if product details can't be fetched
        }
      }
      
      // If we have sizes, use them; otherwise use common defaults
      if (allSizes.size > 0) {
        // Sort sizes naturally (XS, S, M, L, XL, XXL, then numeric)
        const sortedSizes = Array.from(allSizes).sort((a, b) => {
          const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
          const aIndex = sizeOrder.indexOf(a.toUpperCase());
          const bIndex = sizeOrder.indexOf(b.toUpperCase());
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });
        setSizes(sortedSizes);
      } else {
        // Fallback to common sizes
        setSizes(['XS', 'S', 'M', 'L', 'XL', 'XXL']);
      }
    } catch (error) {
      console.error('Error fetching sizes:', error);
      // Fallback to default sizes
      setSizes(['XS', 'S', 'M', 'L', 'XL', 'XXL']);
    }
  };

  const fetchMaterials = async () => {
    try {
      // Fetch all products to get unique fabric types
      const response = await api.get('/products?is_active=true&limit=1000');
      const allProducts = response.products || [];
      const allMaterials = new Set();
      
      allProducts.forEach((product) => {
        if (product.fabric_type) {
          // Split by comma if multiple materials
          const materials = product.fabric_type.split(',').map((m) => m.trim());
          materials.forEach((material) => {
            if (material) allMaterials.add(material);
          });
        }
      });
      
      const sortedMaterials = Array.from(allMaterials).sort();
      setMaterials(sortedMaterials);
    } catch (error) {
      console.error('Error fetching materials:', error);
      // Fallback to default materials
      setMaterials(['Linen', 'Hemp', 'Cotton', 'Polyester', 'Wool', 'Silk']);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await api.get('/products?is_active=true&limit=1000');
      const allProducts = response.products || [];
      const allTypes = new Set();
      
      allProducts.forEach((product) => {
        if (product.product_type) {
          const types = product.product_type.split(',').map((t) => t.trim());
          types.forEach((type) => {
            if (type) allTypes.add(type);
          });
        }
      });
      
      const sortedTypes = Array.from(allTypes).sort();
      setProductTypes(sortedTypes);
    } catch (error) {
      console.error('Error fetching product types:', error);
    }
  };

  const fetchTextures = async () => {
    try {
      const response = await api.get('/products?is_active=true&limit=1000');
      const allProducts = response.products || [];
      const allTextures = new Set();
      
      allProducts.forEach((product) => {
        if (product.texture) {
          const textures = product.texture.split(',').map((t) => t.trim());
          textures.forEach((texture) => {
            if (texture) allTextures.add(texture);
          });
        }
      });
      
      const sortedTextures = Array.from(allTextures).sort();
      setTextures(sortedTextures);
    } catch (error) {
      console.error('Error fetching textures:', error);
    }
  };

  const fetchOccasions = async () => {
    try {
      const response = await api.get('/products?is_active=true&limit=1000');
      const allProducts = response.products || [];
      const allOccasions = new Set();
      
      allProducts.forEach((product) => {
        if (product.occasion) {
          const occasions = product.occasion.split(',').map((o) => o.trim());
          occasions.forEach((occasion) => {
            if (occasion) allOccasions.add(occasion);
          });
        }
      });
      
      const sortedOccasions = Array.from(allOccasions).sort();
      setOccasions(sortedOccasions);
    } catch (error) {
      console.error('Error fetching occasions:', error);
    }
  };

  const fetchPatterns = async () => {
    try {
      const response = await api.get('/products?is_active=true&limit=1000');
      const allProducts = response.products || [];
      const allPatterns = new Set();
      
      allProducts.forEach((product) => {
        if (product.pattern) {
          const patterns = product.pattern.split(',').map((p) => p.trim());
          patterns.forEach((pattern) => {
            if (pattern) allPatterns.add(pattern);
          });
        }
      });
      
      const sortedPatterns = Array.from(allPatterns).sort();
      setPatterns(sortedPatterns);
    } catch (error) {
      console.error('Error fetching patterns:', error);
    }
  };

  const updateURLParams = (newFilters) => {
    const params = new URLSearchParams();
    
    if (newFilters.category_id) params.set('category', newFilters.category_id);
    else params.delete('category');
    if (newFilters.subcategory_id) params.set('subcategory', newFilters.subcategory_id);
    else params.delete('subcategory');
    if (newFilters.l3_category_id) params.set('l3', newFilters.l3_category_id);
    else params.delete('l3');
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.sizes && newFilters.sizes.length > 0) params.set('sizes', newFilters.sizes.join(','));
    if (newFilters.colors && newFilters.colors.length > 0) params.set('colors', newFilters.colors.join(','));
    if (newFilters.materials && newFilters.materials.length > 0) params.set('materials', newFilters.materials.join(','));
    if (newFilters.productTypes && newFilters.productTypes.length > 0) params.set('product_types', newFilters.productTypes.join(','));
    if (newFilters.textures && newFilters.textures.length > 0) params.set('textures', newFilters.textures.join(','));
    if (newFilters.occasions && newFilters.occasions.length > 0) params.set('occasions', newFilters.occasions.join(','));
    if (newFilters.patterns && newFilters.patterns.length > 0) params.set('patterns', newFilters.patterns.join(','));
    if (newFilters.minPrice !== null && newFilters.minPrice !== undefined) params.set('minPrice', newFilters.minPrice.toString());
    if (newFilters.maxPrice !== null && newFilters.maxPrice !== undefined) params.set('maxPrice', newFilters.maxPrice.toString());
    if (showOnlySale) params.set('filter', 'sale');
    else if (showOnlyBestsellers) params.set('filter', 'bestsellers');
    else if (showOnlyNew) params.set('filter', 'new');
    else if (showOnlyCombo) params.set('filter', 'combo');
    else params.delete('filter');
    
    if (sortBy !== 'featured') params.set('sort', sortBy);
    if (page > 1) params.set('page', page.toString());
    
    setSearchParams(params);
  };

  // Find category in tree by id
  const findCategoryById = (cats, id) => {
    if (!cats || !id) return null;
    const numId = parseInt(id, 10);
    for (const c of cats) {
      if (c.id === numId) return c;
      const found = findCategoryById(c.children || [], id);
      if (found) return found;
    }
    return null;
  };

  const handleCategoryToggle = (categoryId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newFilters = {
      ...filters,
      category_id: filters.category_id === categoryId ? '' : categoryId,
      subcategory_id: '', // clear subcategory and L3 when changing category
      l3_category_id: '',
    };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  // Browse by category from empty state: set category and clear search so results show
  const handleBrowseCategory = (categoryId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newFilters = {
      ...filters,
      category_id: categoryId,
      subcategory_id: '',
      l3_category_id: '',
      search: '',
    };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleSubcategoryToggle = (subcategoryId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newFilters = {
      ...filters,
      subcategory_id: filters.subcategory_id === subcategoryId ? '' : subcategoryId,
      l3_category_id: '', // clear L3 when changing subcategory
    };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleL3CategoryToggle = (l3Id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newFilters = {
      ...filters,
      l3_category_id: filters.l3_category_id === l3Id ? '' : l3Id,
    };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleSizeToggle = (size, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newSizes = filters.sizes.includes(size)
      ? filters.sizes.filter((s) => s !== size)
      : [...filters.sizes, size];
    const newFilters = { ...filters, sizes: newSizes };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleColorToggle = (colorId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newColors = filters.colors.includes(colorId)
      ? filters.colors.filter((c) => c !== colorId)
      : [...filters.colors, colorId];
    const newFilters = { ...filters, colors: newColors };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleMaterialToggle = (material, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newMaterials = filters.materials.includes(material)
      ? filters.materials.filter((m) => m !== material)
      : [...filters.materials, material];
    const newFilters = { ...filters, materials: newMaterials };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleProductTypeToggle = (productType, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newProductTypes = (filters.productTypes || []).includes(productType)
      ? (filters.productTypes || []).filter((t) => t !== productType)
      : [...(filters.productTypes || []), productType];
    const newFilters = { ...filters, productTypes: newProductTypes };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleTextureToggle = (texture, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newTextures = (filters.textures || []).includes(texture)
      ? (filters.textures || []).filter((t) => t !== texture)
      : [...(filters.textures || []), texture];
    const newFilters = { ...filters, textures: newTextures };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handleOccasionToggle = (occasion, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newOccasions = (filters.occasions || []).includes(occasion)
      ? (filters.occasions || []).filter((o) => o !== occasion)
      : [...(filters.occasions || []), occasion];
    const newFilters = { ...filters, occasions: newOccasions };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const handlePatternToggle = (pattern, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newPatterns = (filters.patterns || []).includes(pattern)
      ? (filters.patterns || []).filter((p) => p !== pattern)
      : [...(filters.patterns || []), pattern];
    const newFilters = { ...filters, patterns: newPatterns };
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const MAX_PRICE_LIMIT = 10000;

  const MAX_MIN_PRICE = 9999;

  const handlePriceChange = (type, value) => {
    if (value === '' || value === null || value === undefined) {
      setPriceRange((prev) => ({ ...prev, [type]: '' }));
      return;
    }
    let numValue = parseInt(value, 10);
    if (Number.isNaN(numValue)) return;
    if (type === 'maxPrice') numValue = Math.min(MAX_PRICE_LIMIT, Math.max(0, numValue));
    if (type === 'minPrice') numValue = Math.min(MAX_MIN_PRICE, Math.max(0, numValue));
    setPriceRange((prev) => {
      const prevMin = prev.minPrice !== null && prev.minPrice !== undefined && prev.minPrice !== '' ? prev.minPrice : 0;
      const prevMax = prev.maxPrice !== null && prev.maxPrice !== undefined && prev.maxPrice !== '' ? prev.maxPrice : MAX_PRICE_LIMIT;
      const newRange = { ...prev, [type]: numValue };
      if (type === 'minPrice' && numValue > prevMax) newRange.maxPrice = numValue;
      if (type === 'maxPrice' && numValue < prevMin) newRange.minPrice = numValue;
      return newRange;
    });
  };

  const handlePriceEnd = () => {
    let currentMin = priceRange.minPrice === '' || priceRange.minPrice === null || priceRange.minPrice === undefined
      ? 0
      : Number(priceRange.minPrice);
    let currentMax = priceRange.maxPrice === '' || priceRange.maxPrice === null || priceRange.maxPrice === undefined
      ? MAX_PRICE_LIMIT
      : Number(priceRange.maxPrice);

    if (Number.isNaN(currentMin)) currentMin = 0;
    if (Number.isNaN(currentMax)) currentMax = MAX_PRICE_LIMIT;
    currentMin = Math.min(MAX_MIN_PRICE, Math.max(0, currentMin));
    currentMax = Math.min(MAX_PRICE_LIMIT, Math.max(0, currentMax));

    if (currentMin > currentMax) {
      toast.info('Min was greater than max; adjusted to valid range.');
      currentMax = currentMin;
    }

    const newMinPrice = (currentMin === 0 && currentMax === MAX_PRICE_LIMIT) ? null : currentMin;
    const newMaxPrice = (currentMin === 0 && currentMax === MAX_PRICE_LIMIT) ? null : currentMax;

    const newFilters = { ...filters, minPrice: newMinPrice, maxPrice: newMaxPrice };
    setFilters(newFilters);
    setPriceRange({ minPrice: newMinPrice, maxPrice: newMaxPrice });
    setPage(1);
    updateURLParams(newFilters);
  };


  const toggleFilterCollapse = (filterName) => {
    setCollapsedFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const removeFilter = (type, value) => {
    let newFilters = { ...filters };
    if (type === 'category') {
      newFilters = { ...filters, category_id: '', subcategory_id: '', l3_category_id: '' };
    } else if (type === 'subcategory') {
      newFilters = { ...filters, subcategory_id: '', l3_category_id: '' };
    } else if (type === 'l3_category') {
      newFilters = { ...filters, l3_category_id: '' };
    } else if (type === 'new') {
      setShowOnlyNew(false);
      const params = new URLSearchParams(searchParams);
      params.delete('filter');
      setSearchParams(params);
      return;
    } else if (type === 'bestsellers') {
      setShowOnlyBestsellers(false);
      const params = new URLSearchParams(searchParams);
      params.delete('filter');
      setSearchParams(params);
      return;
    } else if (type === 'sale') {
      setShowOnlySale(false);
      const params = new URLSearchParams(searchParams);
      params.delete('filter');
      setSearchParams(params);
      return;
    } else if (type === 'combo') {
      setShowOnlyCombo(false);
      const params = new URLSearchParams(searchParams);
      params.delete('filter');
      setSearchParams(params);
      return;
    } else if (type === 'size') {
      newFilters = { ...filters, sizes: filters.sizes.filter((s) => s !== value) };
    } else if (type === 'color') {
      newFilters = { ...filters, colors: filters.colors.filter((c) => c !== value) };
    } else if (type === 'material') {
      newFilters = { ...filters, materials: filters.materials.filter((m) => m !== value) };
    } else if (type === 'productType') {
      newFilters = { ...filters, productTypes: (filters.productTypes || []).filter((t) => t !== value) };
    } else if (type === 'texture') {
      newFilters = { ...filters, textures: (filters.textures || []).filter((t) => t !== value) };
    } else if (type === 'occasion') {
      newFilters = { ...filters, occasions: (filters.occasions || []).filter((o) => o !== value) };
    } else if (type === 'pattern') {
      newFilters = { ...filters, patterns: (filters.patterns || []).filter((p) => p !== value) };
    } else if (type === 'price') {
      newFilters = { ...filters, minPrice: null, maxPrice: null };
      setPriceRange({ minPrice: null, maxPrice: null });
    }
    setFilters(newFilters);
    setPage(1);
    updateURLParams(newFilters);
  };

  const clearAllFilters = () => {
    const newFilters = {
      category_id: '',
      subcategory_id: '',
      l3_category_id: '',
      sizes: [],
      colors: [],
      materials: [],
      productTypes: [],
      textures: [],
      occasions: [],
      patterns: [],
      minPrice: null,
      maxPrice: null,
    };
    setFilters(newFilters);
    setPriceRange({ minPrice: null, maxPrice: null });
    setShowOnlySale(false);
    setShowOnlyBestsellers(false);
    setShowOnlyNew(false);
    setShowOnlyCombo(false);
    setPage(1);
    setSearchParams(new URLSearchParams());
  };

  const getActiveFilters = () => {
    const active = [];
    if (filters.category_id) {
      const category = findCategoryById(categories, filters.category_id);
      if (category)
        active.push({ type: 'category', value: filters.category_id, label: category.name });
    }
    if (filters.subcategory_id) {
      const sub = findCategoryById(categories, filters.subcategory_id);
      if (sub)
        active.push({ type: 'subcategory', value: filters.subcategory_id, label: sub.name });
    }
    if (filters.l3_category_id) {
      const l3 = findCategoryById(categories, filters.l3_category_id);
      if (l3)
        active.push({ type: 'l3_category', value: filters.l3_category_id, label: l3.name });
    }
    if (showOnlyNew) {
      active.push({ type: 'new', value: 'new', label: 'NEW' });
    }
    if (showOnlyBestsellers) {
      active.push({ type: 'bestsellers', value: 'bestsellers', label: 'BESTSELLERS' });
    }
    if (showOnlySale) {
      active.push({ type: 'sale', value: 'sale', label: 'SALE' });
    }
    if (showOnlyCombo) {
      active.push({ type: 'combo', value: 'combo', label: 'COMBOS' });
    }
    filters.sizes.forEach((size) => {
      active.push({ type: 'size', value: size, label: size });
    });
    filters.colors.forEach((colorId) => {
      const color = colors.find((c) => c.id === colorId);
      if (color) active.push({ type: 'color', value: colorId, label: color.name });
    });
    filters.materials.forEach((material) => {
      active.push({ type: 'material', value: material, label: material });
    });
    (filters.productTypes || []).forEach((productType) => {
      active.push({ type: 'productType', value: productType, label: productType });
    });
    (filters.textures || []).forEach((texture) => {
      active.push({ type: 'texture', value: texture, label: texture });
    });
    (filters.occasions || []).forEach((occasion) => {
      active.push({ type: 'occasion', value: occasion, label: occasion });
    });
    (filters.patterns || []).forEach((pattern) => {
      active.push({ type: 'pattern', value: pattern, label: pattern });
    });
    if ((filters.minPrice !== null && filters.minPrice !== undefined) || (filters.maxPrice !== null && filters.maxPrice !== undefined)) {
      active.push({
        type: 'price',
        value: 'price',
        label: `₹${filters.minPrice !== null && filters.minPrice !== undefined ? filters.minPrice : '0'} - ₹${filters.maxPrice !== null && filters.maxPrice !== undefined ? filters.maxPrice : '∞'}`,
      });
    }
    return active;
  };

  if (loading && !(showOnlySale && saleBanners.length > 0)) return null;

  const activeFilters = getActiveFilters();
  const mainCategories = categories.filter((cat) => !cat.parent_id);
  const gridItems = showOnlyCombo
    ? combos
    : mixedPageItems.length > 0
      ? mixedPageItems
      : products;
  return (
    <Container fluid className="products-page">
      {showOnlySale && saleBanners.length > 0 && (
        <section className="sale-banners-section">
          <Carousel
            className="sale-banners-carousel"
            indicators={saleBanners.length > 1}
            controls={saleBanners.length > 1}
            interval={saleBanners.length > 1 ? 3000 : null}
            fade
          >
            {saleBanners.map((banner, index) => (
              <Carousel.Item
                key={banner.id || index}
                onClick={(e) => {
                  const url = getBannerProductsUrl(banner);
                  if (url) {
                    navigate(url);
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                <div className="sale-banner-slide">
                  <CachedImage
                    className="sale-banner-image"
                    src={banner.image_url}
                    alt={banner.title || 'Sale banner'}
                  />
                  {banner.category_id && (
                    <div className="sale-banner-overlay" role="presentation">
                      <Link
                        to={getBannerProductsUrl(banner) || '/products?filter=sale'}
                        className="sale-banner-cta"
                        onClick={(e) => e.stopPropagation()}
                      >
                        SHOP NOW
                      </Link>
                    </div>
                  )}
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </section>
      )}
      {loading ? null : (
      <Row className="products-layout">
        {/* Left Sidebar - Filters */}
        <Col lg={3} className={`filters-sidebar ${showFilters ? 'show' : 'hidden'}`}>
          <div className="filters-content">
            {/* Close Button for Mobile */}
            <button
              type="button"
              className="filters-close-btn"
              onClick={() => setShowFilters(false)}
              aria-label="Close filters"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            {/* Category tree: Category > Subcategory > L3, all collapsible */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('category')}>
                <h6 className="filter-title">Category</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.category ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.category && (
              <div className="filter-options filter-category-tree">
                <FilterButton
                  label="All"
                  isActive={filters.category_id === '' && !filters.subcategory_id && !filters.l3_category_id}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCategoryToggle('', e); }}
                />
                {mainCategories
                  .filter((category) => {
                    const countL1 = allProducts.filter((p) => p.category_id === category.id).length;
                    if (countL1 > 0) return true;
                    const children = category.children || [];
                    return children.some((sub) => {
                      const countL2 = allProducts.filter((p) => p.subcategory_id === sub.id).length;
                      if (countL2 > 0) return true;
                      const l3s = sub.children || [];
                      return l3s.some((l3) => allProducts.filter((p) => p.l3_category_id === l3.id).length > 0);
                    });
                  })
                  .map((category) => {
                  const hasChildren = category.children && category.children.length > 0;
                  const isL1Expanded = expandedCategoryTree[`l1-${category.id}`];
                  const countL1 = allProducts.filter((p) => p.category_id === category.id).length;
                  return (
                    <div key={category.id} className="filter-category-tree-item">
                      <div className="filter-category-tree-row filter-category-tree-row-l1">
                        
                        <FilterButton
                          label={category.name}
                          isActive={filters.category_id === category.id.toString() && !filters.subcategory_id && !filters.l3_category_id}
                          onClick={(e) => handleCategoryToggle(category.id.toString(), e)}
                          count={countL1}
                        />
                        {hasChildren ? (
                          <button
                            type="button"
                            className="filter-category-tree-chevron"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleCategoryTreeExpand(`l1-${category.id}`); }}
                            aria-label={isL1Expanded ? 'Collapse' : 'Expand'}
                          >
                            <span className={isL1Expanded ? '' : 'filter-chevron-collapsed'}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                                <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                              </svg>
                            </span>
                          </button>
                        ) : (
                          <span className="filter-category-tree-chevron-placeholder" />
                        )}
                      </div>
                      {hasChildren && isL1Expanded && (
                        <div className="filter-category-tree-children">
                          {(category.children || [])
                            .filter((sub) => {
                              const countL2 = allProducts.filter((p) => p.subcategory_id === sub.id).length;
                              if (countL2 > 0) return true;
                              const l3s = sub.children || [];
                              return l3s.some((l3) => allProducts.filter((p) => p.l3_category_id === l3.id).length > 0);
                            })
                            .map((sub) => {
                            const hasL3 = sub.children && sub.children.length > 0;
                            const isL2Expanded = expandedCategoryTree[`l2-${sub.id}`];
                            const countL2 = allProducts.filter((p) => p.subcategory_id === sub.id).length;
                            return (
                              <div key={sub.id} className="filter-category-tree-item filter-category-tree-item-l2">
                                <div className="filter-category-tree-row filter-category-tree-row-l2">
                                 
                                  <FilterButton
                                    label={sub.name}
                                    isActive={filters.subcategory_id === sub.id.toString() && !filters.l3_category_id}
                                    onClick={(e) => handleSubcategoryToggle(sub.id.toString(), e)}
                                    count={countL2}
                                  />
                                   {hasL3 ? (
                                    <button
                                      type="button"
                                      className="filter-category-tree-chevron"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleCategoryTreeExpand(`l2-${sub.id}`); }}
                                      aria-label={isL2Expanded ? 'Collapse' : 'Expand'}
                                    >
                                      <span className={isL2Expanded ? '' : 'filter-chevron-collapsed'}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                                          <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                                        </svg>
                                      </span>
                                    </button>
                                  ) : (
                                    <span className="filter-category-tree-chevron-placeholder" />
                                  )}
                                </div>
                                {hasL3 && isL2Expanded && (
                                  <div className="filter-category-tree-children filter-category-tree-l3">
                                    {(sub.children || [])
                                      .filter((l3) => allProducts.filter((p) => p.l3_category_id === l3.id).length > 0)
                                      .map((l3) => {
                                      const countL3 = allProducts.filter((p) => p.l3_category_id === l3.id).length;
                                      return (
                                        <FilterButton
                                          key={l3.id}
                                          label={l3.name}
                                          isActive={filters.l3_category_id === l3.id.toString()}
                                          onClick={(e) => handleL3CategoryToggle(l3.id.toString(), e)}
                                          count={countL3}
                                        />
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            {/* Size */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('size')}>
                <h6 className="filter-title">Size</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.size ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.size && (
              <div className="size-buttons">
                {sizes.map((size) => (
                  <SizeButton
                    key={size}
                    size={size}
                    isActive={filters.sizes.includes(size)}
                    onClick={(e) => handleSizeToggle(size, e)}
                  />
                ))}
              </div>
              )}
            </div>

            {/* Price Range */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('price')}>
                <h6 className="filter-title">Price Range</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.price ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.price && (
                <PriceRangeFilter
                  minPrice={priceRange.minPrice === '' ? '' : (priceRange.minPrice ?? 0)}
                  maxPrice={priceRange.maxPrice === '' ? '' : (priceRange.maxPrice ?? 10000)}
                  minValue={0}
                  maxValue={10000}
                  onMinChange={(value) => handlePriceChange('minPrice', value)}
                  onMaxChange={(value) => handlePriceChange('maxPrice', value)}
                  onPriceEnd={handlePriceEnd}
                />
              )}
            </div>

            {/* Colors */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('color')}>
                <h6 className="filter-title">Colors</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.color ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.color && (
              <div className="color-chips-container">
                {colors.map((color) => (
                  <ColorChip
                    key={color.id}
                    color={color}
                    isActive={filters.colors.includes(color.id)}
                    onClick={(e) => handleColorToggle(color.id, e)}
                  />
                ))}
              </div>
              )}
            </div>

            {/* Material */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('material')}>
                <h6 className="filter-title">Material</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.material ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.material && (
              <div className="material-checkboxes">
                {materials.map((material) => (
                  <MaterialCheckbox
                    key={material}
                    material={material}
                    isChecked={filters.materials.includes(material)}
                    onChange={(e) => handleMaterialToggle(material, e)}
                  />
                ))}
              </div>
              )}
            </div>

            {/* Product Type */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('productType')}>
                <h6 className="filter-title">Product Type</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.productType ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.productType && (
              <div className="material-checkboxes">
                {productTypes.map((productType) => (
                  <MaterialCheckbox
                    key={productType}
                    material={productType}
                    isChecked={(filters.productTypes || []).includes(productType)}
                    onChange={(e) => handleProductTypeToggle(productType, e)}
                  />
                ))}
              </div>
              )}
            </div>

            {/* Texture */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('texture')}>
                <h6 className="filter-title">Texture</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.texture ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.texture && (
              <div className="material-checkboxes">
                {textures.map((texture) => (
                  <MaterialCheckbox
                    key={texture}
                    material={texture}
                    isChecked={(filters.textures || []).includes(texture)}
                    onChange={(e) => handleTextureToggle(texture, e)}
                  />
                ))}
              </div>
              )}
            </div>

            {/* Occasion */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('occasion')}>
                <h6 className="filter-title">Occasion</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.occasion ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.occasion && (
              <div className="material-checkboxes">
                {occasions.map((occasion) => (
                  <MaterialCheckbox
                    key={occasion}
                    material={occasion}
                    isChecked={(filters.occasions || []).includes(occasion)}
                    onChange={(e) => handleOccasionToggle(occasion, e)}
                  />
                ))}
              </div>
              )}
            </div>

            {/* Pattern */}
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleFilterCollapse('pattern')}>
                <h6 className="filter-title">Pattern</h6>
                <span className={`filter-toggle-icon ${collapsedFilters.pattern ? '' : 'rotated'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M0.170067 6.49437C0.376209 6.7032 0.698787 6.72219 0.926099 6.55132L0.991223 6.49437L6 1.42038L11.0088 6.49437C11.2149 6.7032 11.5375 6.72219 11.7648 6.55132L11.8299 6.49437C12.0361 6.28553 12.0548 5.95873 11.8862 5.72845L11.8299 5.66248L6.41058 0.172281C6.20444 -0.0365559 5.88186 -0.0555413 5.65455 0.115325L5.58942 0.172281L0.170067 5.66248C-0.056689 5.8922 -0.056689 6.26465 0.170067 6.49437Z" fill="#1F1F1F"/>
                  </svg>
                </span>
              </div>
              {!collapsedFilters.pattern && (
              <div className="material-checkboxes">
                {patterns.map((pattern) => (
                  <MaterialCheckbox
                    key={pattern}
                    material={pattern}
                    isChecked={(filters.patterns || []).includes(pattern)}
                    onChange={(e) => handlePatternToggle(pattern, e)}
                  />
                ))}
              </div>
              )}
            </div>
          </div>
        </Col>

        {/* Main Content - Product / Combo Grid */}
        <Col lg={9} className="products-grid-col">
          {/* Top Filter Bar */}
           {activeFilters.length > 0 && (
            <><div className="products-count w-100">
              {showOnlyCombo
                ? `Shop Our Combos (${totalProducts})`
                : `Products Based on Your selection (${totalProducts})`}
            </div>
            </>
            )}
          {showOnlyCombo && activeFilters.length === 0 && (
            <div className="products-count w-100">Shop Our Combos ({totalProducts})</div>
          )}
          <div className="products-top-bar">
           
            <div className="top-bar-left">
              <Button
                type="button"
                variant="outline-none"
                className={`filters-toggle-btn p-0 ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M4 21V14"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4 10V3"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12 21V12"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12 8V3"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M20 21V16"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M20 12V3"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M1 14H7"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9 8H15"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M17 16H23"
                    stroke="#1F1F1F"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Filters
              </Button>
              {!showOnlyCombo && (
                <Form.Check
                  type="checkbox"
                  label="Show only products on sale"
                  checked={showOnlySale}
                  onChange={(e) => setShowOnlySale(e.target.checked)}
                  className="sale-checkbox"
                />
              )}
            </div>
            <div className="top-bar-right">
              <span className="sort-label">Sort by:</span>
              <Form.Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                className="sort-select"
              >
                {!showOnlyCombo && <option value="featured">Featured</option>}
                {!showOnlyCombo && <option value="best_selling">Best Selling</option>}
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="newest">Newest</option>
              </Form.Select>
            </div>
          </div>

          {/* Active Filters Chips */}
          {activeFilters.length > 0 && (<>
           
            <div className="active-filters-bar">
              <div className="active-filters-chips">
                {activeFilters.map((filter, index) => (
                  <div
                    key={index}
                    className="filter-chip"
                    onClick={() => removeFilter(filter.type, filter.value)}
                  >
                    <span className="chip-close">×</span>
                    {filter.label.toLowerCase()}   
                  </div>
                ))}
                <div className="filter-chip clear-all-chip" onClick={clearAllFilters}>
                  <span className="chip-close">×</span>
                  CLEAR ALL
                </div>
              </div>
            </div>
         </>  )}
          {gridItems.length === 0 ? (
            <div className="no-products-empty">
              <div className="no-products-empty-inner">
                <div className="no-products-empty-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </div>
                <h2 className="no-products-empty-title">
                  {showOnlyCombo ? 'No combos found' : 'No products found'}
                </h2>
                <p className="no-products-empty-message">
                  {showOnlyCombo
                    ? 'Check back soon for new combo offers.'
                    : filters.search
                    ? `No results for "${filters.search}". Try different keywords or browse our categories below.`
                    : 'Try adjusting your filters, or browse our categories below.'}
                </p>
                {!showOnlyCombo && mainCategories.length > 0 && (
                  <div className="no-products-suggestions">
                    <p className="no-products-categories-label">Browse by category</p>
                    <div className="no-products-category-chips">
                      {mainCategories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          className="no-products-chip"
                          onClick={(e) => handleBrowseCategory(category.id.toString(), e)}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="no-products-clear-btn"
                  onClick={clearAllFilters}
                >
                  {showOnlyCombo ? 'Back to all products' : 'Clear all filters'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <Row className="g-4 sm:mt-2">
                {showOnlyCombo
                  ? combos.map((combo) => (
                      <Col key={`combo-${combo.id}`} xs={6} md={4} lg={3} className="mb-4">
                        <ComboCard combo={combo} />
                      </Col>
                    ))
                  : mixedPageItems.length > 0
                    ? mixedPageItems.map((item) =>
                        item.kind === 'combo' ? (
                          <Col key={`combo-${item.data.id}`} xs={6} md={4} lg={3} className="mb-4">
                            <ComboCard combo={item.data} />
                          </Col>
                        ) : (
                          <Col
                            key={`product-${item.data.listing_key || `${item.data.id}-${item.data.listing_color_id || 'default'}`}`}
                            xs={6}
                            md={4}
                            lg={3}
                            className="mb-4"
                          >
                            <ProductCard product={item.data} buttonText="Add to Cart" />
                          </Col>
                        )
                      )
                    : (
                      <>
                        {products.map((product) => (
                          <Col
                            key={`product-${product.listing_key || `${product.id}-${product.listing_color_id || 'default'}`}`}
                            xs={6}
                            md={4}
                            lg={3}
                            className="mb-4"
                          >
                            <ProductCard product={product} buttonText="Add to Cart" />
                          </Col>
                        ))}
                      </>
                    )}
              </Row>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper">
                  <div className="pagination">
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>
                    {getVisiblePages(page, totalPages).map((item, idx) =>
                      item === '…' ? (
                        <span key={`ellipsis-${idx}`} className="pagination-ellipsis" aria-hidden="true">
                          …
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={item}
                          className={`pagination-btn ${page === item ? 'active' : ''}`}
                          onClick={() => setPage(item)}
                          aria-current={page === item ? 'page' : undefined}
                        >
                          {item}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
      )}
    </Container>
  );
};

export default Products;
